import axios from "axios";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { startSignOutEvent, type TSignOutReason } from "@/lib/auth-analytics";
import { clearBootstrap } from "@/lib/launchdarkly/client";
import { peekQueryClient } from "@/queries/client";
import { notificationSettingsKey } from "@/queries/notification-settings";
import { scopeFor, ScopeNotReadyError, syncQueryPrincipal } from "@/queries/scope";

/** Profile keys that record where a person is, not a preference they set: the
 *  last workspace (the switcher's own event covers it) and the onboarding flag
 *  (onboarding_completed). */
const PROFILE_BOOKKEEPING_KEYS = new Set(["last_workspace_id", "is_onboarded"]);
import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";

/** Thrown by `fetchCurrentUser` when `users/session/` answers 200 with
 *  `is_authenticated: false` — the server has told us there is no session, as
 *  opposed to failing to tell us anything. Callers that already treat a throw as
 *  "render the signed-out view" need no change; auth-guard reads
 *  `UserStore.sessionKnownAbsent` rather than catching this. */
export class SessionAbsentError extends Error {
  constructor() {
    super("No authenticated session");
    this.name = "SessionAbsentError";
  }
}
import type { INotificationSetting, IUser, UserProfile, UserSettings } from "@/types/user";

export class UserStore {
  currentUser: IUser | null = null;
  profile: UserProfile | null = null;
  settings: UserSettings | null = null;
  isInstanceAdmin = false;
  /**
   * The concrete actions the user's instance-scope grants carry (`instance.sales`
   * holds the Cognota-only workspace lifecycle verbs, NG-1237). Read this — not the
   * permissions copied into workspace payloads — to answer "may this user create a
   * workspace": a rep who belongs to no workspace yet has an empty workspace list
   * and would otherwise never be offered Create Workspace. For an instance admin it
   * lists what their wildcard allows at the instance scope (NG-2144); the gates still
   * ask `isInstanceAdmin` too, as they did while the array left the wildcard out.
   */
  instancePermissions: string[] = [];
  isLoading = false;
  error: string | null = null;
  /**
   * HTTP status of the most recent fetchCurrentUser() failure, or `null` if
   * the last call succeeded / no call has run / the failure had no status
   * (network error, CORS, BE container restarting) — including a session that
   * is simply absent, which `users/session/` reports as a 200 rather than an
   * error (NG-1338). Auth-guard reads `sessionKnownAbsent` rather than this;
   * this stays the diagnostic of *how* a fetch failed.
   */
  errorStatus: number | null = null;
  /**
   * `true` once the server has *told* us there is no session: a 200 from
   * `users/session/` with `is_authenticated: false`, or a 401/403 from it.
   * `false` while a session is live and — this is the point — after a failure
   * that says nothing about the session (5xx, network error, the dev backend
   * bouncing). Auth-guard redirects on this rather than on `errorStatus`, so a
   * blip leaves the retry UI up instead of kicking the user to sign-in.
   */
  sessionKnownAbsent = false;
  /**
   * When `users/session/` last confirmed a live session (epoch ms), or null.
   * The auth guard stopped re-reading the session on every navigation
   * (NG-2410), so this is what tells it the session has gone unchecked for a
   * while: it re-reads when the tab comes back after `SESSION_REREAD_AFTER_MS`.
   */
  sessionReadAt: number | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // exception: autoBind intentionally omitted — user-menu.test.tsx and
    // command-palette.test.tsx monkey-patch `signOut`. action.bound installs
    // bound actions as non-configurable, so those mocks would break. Tracked
    // as a follow-up; refactor tests to mock at the authService boundary.
    makeAutoObservable(this, { rootStore: false } as never);
  }

  get isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  get isOnboarded(): boolean {
    return this.profile?.is_onboarded ?? false;
  }

  /** NG-1373: mirrors `isOnboarded` — computed fresh from the session
   *  response on every fetch, never cached client-side, so a workspace
   *  turning expiration off (or the user changing their password) is
   *  reflected on the very next fetchCurrentUser() rather than staying
   *  stuck until some separate invalidation. */
  get mustChangePassword(): boolean {
    return this.currentUser?.password_expired ?? false;
  }

  hasInstanceCapability(action: string): boolean {
    return this.instancePermissions.includes(action);
  }

  /**
   * Resolve the whole shell bootstrap from `users/session/` (NG-1338).
   *
   * This used to be a `Promise.all` of `users/me/`, its profile, its settings and
   * its instance standing. Logged out that was one 200 and three guaranteed 403s
   * on every guarded route entry, and the boot path is 61.6% of all API traffic
   * (`docs/design/frontend-request-demand-baseline.md`, Finding A). The composed
   * endpoint answers both cases in one request.
   *
   * It still **throws** when there is no session, because `home.tsx` and
   * `workspace-invitations.tsx` both read a rejection as "treat them as signed
   * out". What changed is that the no-session case is now a 200, so the throw is
   * ours: `SessionAbsentError`.
   */
  async fetchCurrentUser(): Promise<IUser> {
    this.isLoading = true;
    this.error = null;
    this.errorStatus = null;
    try {
      const session = await this.rootStore.userService.session();
      if (!session.is_authenticated || !session.user) {
        runInAction(() => {
          this.currentUser = null;
          this.profile = null;
          this.settings = null;
          this.isInstanceAdmin = false;
          this.instancePermissions = [];
          this.sessionKnownAbsent = true;
        });
        syncQueryPrincipal(null, peekQueryClient());
        throw new SessionAbsentError();
      }
      const user = session.user;
      const profile = session.profile ?? null;
      const settings = session.settings ?? null;
      // A different user than the cache holds (account switch) clears it.
      syncQueryPrincipal(user.id, peekQueryClient());
      runInAction(() => {
        this.sessionKnownAbsent = false;
        this.sessionReadAt = Date.now();
        this.currentUser = user;
        this.profile = profile;
        this.settings = settings;
        this.isInstanceAdmin = session.instance?.is_instance_admin ?? false;
        this.instancePermissions = session.instance?.instance_permissions ?? [];
        // Keep the loaded member rows in sync so avatar changes are reflected
        // immediately in member pickers, capacity views, etc., and in the
        // cached member lists the pickers read with `query-cache` on (NG-2797).
        this.rootStore.workspaceMembers.patchMemberUser(user.id, {
          avatar_url: user.avatar_url,
        });
      });
      return user;
    } catch (err) {
      if (err instanceof SessionAbsentError) throw err;
      // A 401 or 403 on /users/session/ means the session is gone — DRF returns
      // 403 (not 401) for unauthenticated session-auth requests by default,
      // so we have to treat both as "logged out" to avoid the stuck-UI state
      // where every API call 403s but the FE still thinks it's signed in.
      // 5xx, network errors, and anything else (e.g. the dev backend
      // bouncing on a code change) leave currentUser intact so auth-guard
      // surfaces the retry UI instead of redirecting to the sign-in page.
      // See PR #557 + #558 — first pass only handled 401 and left users
      // stuck whenever the BE invalidated their session row.
      const status = axios.isAxiosError(err) ? (err.response?.status ?? null) : null;
      const isUnauthenticated = status === 401 || status === 403;
      runInAction(() => {
        this.error = "user.fetch_error";
        this.errorStatus = status;
        if (isUnauthenticated) {
          this.currentUser = null;
          this.sessionKnownAbsent = true;
        }
      });
      if (isUnauthenticated) syncQueryPrincipal(null, peekQueryClient());
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateCurrentUser(data: Partial<IUser>): Promise<IUser> {
    // Read before the write: `fields` is what this save changed (NG-2068).
    const fields = changedFields(data, this.currentUser ?? undefined);
    const user = await this.rootStore.userService.updateCurrentUser(data);
    runInAction(() => {
      this.currentUser = user;
    });
    track("profile_updated", { fields, source: "page" });
    return user;
  }

  async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    const fields = changedFields(data, this.profile ?? undefined).filter(
      (key) => !PROFILE_BOOKKEEPING_KEYS.has(key),
    );
    const profile = await this.rootStore.userService.updateCurrentUserProfile(data);
    runInAction(() => {
      this.profile = profile;
    });
    if (fields.length > 0) track("preferences_updated", { fields, source: "page" });
    return profile;
  }

  async uploadAvatar(file: File): Promise<IUser> {
    await this.rootStore.userAssetService.uploadUserImage("USER_AVATAR", file);
    // Before the re-read, so a failed refresh can't drop it.
    track("profile_avatar_uploaded", { source: "page" });
    return this.fetchCurrentUser();
  }

  async removeAvatar(assetId: string): Promise<IUser> {
    await this.rootStore.userAssetService.deleteAsset(assetId);
    track("profile_avatar_removed", { source: "page" });
    return this.fetchCurrentUser();
  }

  async generateEmailChangeCode(email: string): Promise<{ code: string }> {
    const result = await this.rootStore.userService.generateEmailChangeCode(email);
    track("email_change_requested");
    return result;
  }

  async updateEmail(email: string, code: string): Promise<IUser> {
    const user = await this.rootStore.userService.updateEmail(email, code);
    runInAction(() => {
      this.currentUser = user;
    });
    track("email_changed", { source: "page" });
    return user;
  }

  /**
   * Write one switch change through `send`, against `workspaceSlug`'s cached
   * notification settings (`app/queries/notification-settings.ts`). Optimistic:
   * the switch flips in the entry now, the list goes back on failure, and the
   * answer replaces it, since it carries every category's resolved value. A
   * read in flight is cancelled first, so an answer sent before the flip
   * cannot land over it. Rethrows a failure for the caller's toast.
   */
  private async writeNotificationSettings(
    workspaceSlug: string,
    flip: (setting: INotificationSetting) => INotificationSetting,
    send: () => Promise<{ settings: INotificationSetting[] }>,
  ): Promise<void> {
    const client = peekQueryClient();
    let key: readonly unknown[] | null = null;
    try {
      key = notificationSettingsKey(scopeFor(workspaceSlug));
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      console.warn("[user] notification switch saved before the session resolved; not cached", {
        workspaceSlug,
      });
    }
    const previous = client && key ? client.getQueryData<INotificationSetting[]>(key) : undefined;
    if (client && key && previous) {
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData<INotificationSetting[]>(key, previous.map(flip));
    }
    try {
      const { settings } = await send();
      if (client && key) client.setQueryData<INotificationSetting[]>(key, settings);
    } catch (error) {
      if (client && key && previous) client.setQueryData<INotificationSetting[]>(key, previous);
      throw error;
    }
  }

  /** Toggle one category. Optimistic: flip locally now, reconcile after, revert on failure. */
  async updateNotificationSetting(
    workspaceSlug: string,
    code: string,
    enabled: boolean,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.writeNotificationSettings(
      workspaceSlug,
      (s) => (s.code === code ? { ...s, enabled } : s),
      () =>
        this.rootStore.userService.updateNotificationSettings(workspaceSlug, { [code]: enabled }),
    );
    track("notification_preference_updated", {
      category: code,
      channel: "in_app",
      enabled,
      source,
    });
  }

  /** Toggle one category's **Teams** switch (NG-1187). Optimistic like its sibling.
   *
   *  Two things it deliberately does not do. It never touches `enabled`: the two
   *  switches are independent, and the endpoint creates a missing row with
   *  `enabled: true` for exactly that reason — a Teams answer that silently muted the
   *  bell is the defect this shape exists to make impossible. And the optimistic flip
   *  clears `teams_is_default`, because the person has just chosen: leaving the hint up
   *  would tell them their explicit answer was inherited until the response landed.
   *
   *  The response replaces the list rather than being merged into it — it carries every
   *  category's resolved value, and resolution is the server's job.
   *
   *  A failure is reverted and re-thrown, not logged here: the form is where the
   *  failure is absorbed into a toast, so it is the place that owns the log line.
   */
  async updateTeamsSetting(
    workspaceSlug: string,
    code: string,
    enabled: boolean,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.writeNotificationSettings(
      workspaceSlug,
      (s) => (s.code === code ? { ...s, teams_enabled: enabled, teams_is_default: false } : s),
      () =>
        this.rootStore.userService.updateTeamsNotificationSettings(workspaceSlug, {
          [code]: enabled,
        }),
    );
    track("notification_preference_updated", {
      category: code,
      channel: "teams",
      enabled,
      source,
    });
  }

  async changePassword(oldPassword: string | undefined, newPassword: string): Promise<void> {
    await this.rootStore.authService.changePassword(oldPassword, newPassword);
    // `first_password`: an account with an auto-set password sends no old one.
    track("password_changed", { first_password: oldPassword === undefined, source: "page" });
  }

  /** `reason` says why: the person chose to, an idle timeout, a deactivation, or
   *  an invitation opened while signed in as someone else.
   *
   *  Rejects if the sign-out could not be sent; otherwise never settles, because
   *  the browser is leaving (see `AuthService.signOut`). Stores are left as they
   *  are: the server's redirect replaces the whole app. Only the flag cache is
   *  cleared, since it outlives the page and the next sign-in would otherwise
   *  start from this user's flags (NG-1278). */
  async signOut(reason: TSignOutReason = "user"): Promise<never> {
    // sign_out_started now; signed_out once the next load finds the session gone.
    startSignOutEvent(reason, this.currentUser?.id ?? null);
    clearBootstrap();
    return this.rootStore.authService.signOut();
  }
}
